import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  CmsFeedSettingsResponse,
  UpdateCmsFeedSettingsRequest,
} from "@/types/generated/cms.generated";

/**
 * Social Feed master switch settings for a single organization.
 * Backed by the CMS API `GET/PUT /organizations/{organizationId}/feed-settings`.
 *
 * When the master switch (`masterFeedEnabled`) is OFF, the employee app
 * receives `feedEnabled = false` regardless of any organization-level
 * feed configuration.
 */

const feedSettingsKey = (organizationId: string | null | undefined) =>
  ["organizations", organizationId, "feed-settings"] as const;

// Fetch the feed settings for an organization
export function useOrgFeedSettings(organizationId: string | null | undefined) {
  return useQuery<CmsFeedSettingsResponse>({
    queryKey: feedSettingsKey(organizationId),
    queryFn: () =>
      apiRequest("GET", `/organizations/${organizationId}/feed-settings`),
    enabled: !!organizationId,
  });
}

// Update the feed settings (master switch + flags) for an organization
export function useUpdateOrgFeedSettings(
  organizationId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateCmsFeedSettingsRequest) =>
      apiRequest<CmsFeedSettingsResponse>(
        "PUT",
        `/organizations/${organizationId}/feed-settings`,
        data,
      ),
    // On-success cache update (not optimistic): the cache is written from the
    // server response after the PUT resolves, so a failed request never leaves
    // stale UI. The switch is disabled while the mutation is pending.
    onSuccess: (updated) => {
      queryClient.setQueryData(feedSettingsKey(organizationId), updated);
      queryClient.invalidateQueries({
        queryKey: feedSettingsKey(organizationId),
      });
      toast({
        title: "Feed settings updated",
        description: updated.masterFeedEnabled
          ? "Social Feed has been enabled for this organization."
          : "Social Feed has been disabled for this organization.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to update feed settings. Please try again.",
        variant: "destructive",
      });
    },
  });
}
